import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertTriangle,
  LayoutDashboard,
  Users,
  Activity,
  BadgeCheck,
  CreditCard,
  Percent,
  Flag,
  Image as ImageIcon,
  LogOut,
  X,
} from "lucide-react";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase";
import { clearStoredAuthSession } from "../../utils/authSession";
import brandLogo from "../../Assets/karanki-new-logo.jpg";

const menus = [
  { name: "Dashboard", icon: LayoutDashboard, path: "/admin/dashboard" },
  { name: "Users", icon: Users, path: "/admin/users" },
  { name: "Presence", icon: Activity, path: "/admin/presence" },
  { name: "Verifications", icon: BadgeCheck, path: "/admin/verifications" },
  { name: "Payments", icon: CreditCard, path: "/admin/payments" },
  { name: "Coupons", icon: Percent, path: "/admin/coupons" },
  { name: "Banners", icon: ImageIcon, path: "/admin/banners" },
  { name: "Reports", icon: Flag, path: "/admin/reports" },
];

const AdminSidebar = ({ open, setOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      clearStoredAuthSession();
      await signOut(auth);
      navigate("/admin/login", { replace: true });
    } finally {
      setLoggingOut(false);
      setShowLogoutModal(false);
    }
  };

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  return (
    <>
      {/* Mobile overlay */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 bg-ink-900/45 backdrop-blur-sm lg:hidden"
          />
        )}
      </AnimatePresence>

      <aside
        className={`fixed left-0 top-0 z-50 flex h-screen w-[280px] flex-col border-r border-sand-200 bg-white shadow-soft transition-transform duration-300 lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Brand */}
        <div className="flex h-[80px] items-center justify-between border-b border-sand-200 px-5">
          <Link to="/admin/dashboard" className="flex items-center gap-3" onClick={() => setOpen(false)}>
            <img
              src={brandLogo}
              alt="Karanki"
              className="h-12 w-12 rounded-2xl object-cover object-center ring-1 ring-sand-200 shadow-soft"
            />
            <div className="leading-tight">
              <p className="font-display text-[15px] font-bold text-ink-800">Karanki</p>
              <p className="text-[10px] font-semibold tracking-wider text-brand-600">ADMIN PANEL</p>
            </div>
          </Link>
          <button onClick={() => setOpen(false)} className="lg:hidden btn-icon h-9 w-9">
            <X size={16} />
          </button>
        </div>

        {/* Menu */}
        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          <p className="px-3 pb-2 pt-3 text-[10.5px] font-semibold uppercase tracking-wider text-ink-300">
            Main menu
          </p>
          {menus.map((menu) => {
            const Icon = menu.icon;
            const active = isActive(menu.path);
            return (
              <Link
                key={menu.name}
                to={menu.path}
                onClick={() => setOpen(false)}
                className={`group relative flex items-center gap-3 rounded-2xl px-3.5 py-3 text-[13.5px] font-semibold transition ${
                  active
                    ? "text-white"
                    : "text-ink-700 hover:bg-sand-100 hover:text-brand-700"
                }`}
              >
                {active && (
                  <motion.span
                    layoutId="admin-active"
                    className="absolute inset-0 -z-10 rounded-2xl bg-brand-gradient shadow-[0_8px_18px_-8px_rgba(201,2,67,0.45)]"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
                <Icon size={18} />
                {menu.name}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="border-t border-sand-200 p-4">
          <button
            type="button"
            onClick={() => setShowLogoutModal(true)}
            className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-white px-4 py-3 text-[13px] font-semibold text-rose-600 transition hover:bg-rose-50"
          >
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>

      {/* Logout Modal */}
      <AnimatePresence>
        {showLogoutModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-ink-900/45 px-4 backdrop-blur-sm"
            onClick={() => !loggingOut && setShowLogoutModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="card-elevated w-full max-w-md overflow-hidden"
            >
              <div className="bg-brand-soft px-6 py-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50 text-rose-600">
                    <AlertTriangle size={24} />
                  </div>
                  <div>
                    <h3 className="font-display text-2xl font-bold text-ink-800">Confirm logout</h3>
                    <p className="mt-1.5 text-[13.5px] leading-6 text-ink-500">
                      You'll be signed out of the admin panel on this device.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 px-6 py-5">
                <button
                  type="button"
                  disabled={loggingOut}
                  onClick={() => setShowLogoutModal(false)}
                  className="btn-ghost"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={loggingOut}
                  onClick={handleLogout}
                  className="btn-primary"
                >
                  {loggingOut ? "Logging out..." : "Yes, logout"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AdminSidebar;
